import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";

const money = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Editable milestones: label + amount. Keeps sum visible vs total.
export const PaymentScheduleEditor = ({ schedule, total, onChange, readOnly, onTogglePaid, onDueDate }) => {
  const sum = schedule.reduce((s, m) => s + Number(m.amount || 0), 0);
  const diff = Math.round((Number(total) - sum) * 100) / 100;
  const update = (i, patch) => onChange(schedule.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  const applyPct = (i, pct) => update(i, { percentage: pct, amount: Math.round(Number(total) * Number(pct)) / 100 });

  return (
    <div className="space-y-2" data-testid="payment-schedule-editor">
      {schedule.map((m, i) => (
        <div key={m.milestone_id || i} className="grid grid-cols-12 gap-2 items-center">
          <span className="col-span-1 text-sm text-slate-500">{String.fromCharCode(97 + i)})</span>
          <Input className="col-span-6" value={m.label} disabled={readOnly} onChange={e => update(i, { label: e.target.value })} data-testid={`ms-label-${i}`} />
          <div className="col-span-2 flex items-center gap-1">
            <Input type="number" className="text-right" value={m.percentage ?? ""} disabled={readOnly} onChange={e => applyPct(i, e.target.value)} data-testid={`ms-pct-${i}`} /><span className="text-xs text-slate-400">%</span>
          </div>
          <Input type="number" step="0.01" className="col-span-2 text-right" value={m.amount} disabled={readOnly} onChange={e => update(i, { amount: Number(e.target.value) })} data-testid={`ms-amount-${i}`} />
          <div className="col-span-1 flex justify-end">
            {onTogglePaid ? (
              <button type="button" onClick={() => onTogglePaid(m)} className={`text-xs px-2 py-1 rounded ${m.status === "paid" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`ms-paid-${i}`}>{m.status === "paid" ? "Paid" : "Mark paid"}</button>
            ) : !readOnly && (
              <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => onChange(schedule.filter((_, idx) => idx !== i))} data-testid={`ms-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
            )}
          </div>
          {onDueDate && m.status !== "paid" && (
            <div className="col-span-11 col-start-2 flex items-center gap-2 -mt-1">
              <span className="text-xs text-slate-400">Due</span>
              <Input type="date" className="h-7 w-40 text-xs" value={m.due_date?.slice(0, 10) || ""} onChange={e => onDueDate(m, e.target.value)} data-testid={`ms-due-${i}`} />
              {m.due_date && <span className="text-[11px] text-slate-400">{m.reminded_due ? "reminder sent (due)" : m.reminded_before ? "reminder sent" : "reminders scheduled"}</span>}
            </div>
          )}
        </div>
      ))}
      <div className="flex items-center justify-between pt-1">
        {!readOnly && !onTogglePaid ? (
          <Button type="button" size="sm" variant="outline" onClick={() => onChange([...schedule, { label: "", percentage: 0, amount: Math.max(diff, 0), status: "pending" }])} data-testid="ms-add"><Plus className="w-3.5 h-3.5 mr-1" /> Add milestone</Button>
        ) : <span />}
        <p className={`text-xs ${Math.abs(diff) > 0.011 ? "text-red-600 font-medium" : "text-slate-500"}`} data-testid="ms-sum">
          Sum ${money(sum)} of ${money(total)}{Math.abs(diff) > 0.011 && ` — ${diff > 0 ? "short" : "over"} by $${money(Math.abs(diff))}`}
        </p>
      </div>
    </div>
  );
};
